/**
 * Autorizaciones en pantalla (spec 01, enmienda 2026-10-09). Cuando el servidor responde 403 `requiere_autorizacion`,
 * se abre este diálogo: el usuario la obtiene con la CLAVE de quien tiene el permiso (aquí mismo) o la pide A DISTANCIA
 * (espera a que la aprueben desde otro usuario) y la operación se reintenta sola con la autorización adjunta.
 *
 *   const aut = useAutorizacion();
 *   await aut.ejecutar((ids) => emitir({ ...datos, autorizaciones: ids }), { motivo, detalle });
 *   …
 *   {aut.dialogo}
 */
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { TIPOS_AUTORIZACION, type AutorizacionDTO, type TipoAutorizacion } from '@mg/shared';
import { KeyRound, Loader2, Send, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { ErrorApi, get, mensajeDeError, post } from '@/lib/api.ts';
import { Button } from './ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog.tsx';
import { Input } from './ui/input.tsx';
import { Campo } from './ui/label.tsx';

export type DetalleAutorizacion = Record<string, string | number | boolean | null>;

interface Pedido {
  tipo: TipoAutorizacion;
  motivo: string;
  detalle: DetalleAutorizacion | undefined;
  montoUsd: string | undefined;
  /** Quién puede autorizarla, según el servidor («la dueña», «un administrador o la dueña»…). */
  quien: string | undefined;
  resolver: (id: string | null) => void;
}

/** ¿El error del servidor pide una autorización? Devuelve el tipo que hace falta. */
export function tipoRequerido(e: unknown): TipoAutorizacion | null {
  if (!(e instanceof ErrorApi) || e.status !== 403) return null;
  // `autorizacion_no_corresponde`: la autorización guardada es por menos de lo que vale ahora la operación (se agregó
  // un producto, subió el descuento): hay que pedir otra por el monto nuevo, no quedarse en «No se pudo emitir»
  const codigos = ['requiere_autorizacion', 'autorizacion_usada', 'autorizacion_vencida', 'autorizacion_negada', 'autorizacion_pendiente', 'autorizacion_no_corresponde'];
  if (!codigos.includes(e.codigo)) return null;
  const tipo = (e.detalles as { tipo?: string } | undefined)?.tipo;
  return tipo && tipo in TIPOS_AUTORIZACION ? (tipo as TipoAutorizacion) : null;
}

/**
 * A quién nombra el servidor como autorizador de ese tipo (re-auditoría 2026-10-18, ronda 2, hallazgo 2-H12): la
 * devolución de dinero la autoriza solo la dueña, no «el administrador».
 */
function quienAutoriza(e: unknown): string | undefined {
  const quien = e instanceof ErrorApi ? (e.detalles as { quien_autoriza?: unknown } | undefined)?.quien_autoriza : undefined;
  return typeof quien === 'string' && quien ? quien : undefined;
}

export function useAutorizacion() {
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [ids, setIds] = useState<string[]>([]);
  const idsRef = useRef<string[]>([]);

  /** Abre el diálogo y se resuelve con el id de la autorización aprobada (o null si se cancela). */
  const pedir = useCallback(
    (tipo: TipoAutorizacion, opciones: { motivo?: string; detalle?: DetalleAutorizacion; montoUsd?: string; quien?: string } = {}) =>
      new Promise<string | null>((resolver) => setPedido({ tipo, motivo: opciones.motivo ?? '', detalle: opciones.detalle, montoUsd: opciones.montoUsd, quien: opciones.quien, resolver })),
    [],
  );

  const limpiar = useCallback(() => {
    idsRef.current = [];
    setIds([]);
  }, []);

  /**
   * Ejecuta la operación con las autorizaciones acumuladas; si el servidor pide una, la solicita y reintenta.
   * Devuelve null si el usuario cancela el diálogo.
   */
  const ejecutar = useCallback(
    async <T,>(operacion: (autorizaciones: string[]) => Promise<T>, opciones: { motivo?: string; detalle?: DetalleAutorizacion; /** Monto por el que se pide; puede depender del tipo (el de un descuento es el descuento, no la venta). */ montoUsd?: string | ((tipo: TipoAutorizacion) => string | undefined) } = {}): Promise<T | null> => {
      for (let intento = 0; intento < 6; intento++) {
        try {
          const r = await operacion(idsRef.current);
          limpiar();
          return r;
        } catch (e) {
          const tipo = tipoRequerido(e);
          if (!tipo) throw e;
          const monto = typeof opciones.montoUsd === 'function' ? opciones.montoUsd(tipo) : opciones.montoUsd;
          const quien = quienAutoriza(e);
          const id = await pedir(tipo, { ...(opciones.motivo !== undefined ? { motivo: opciones.motivo } : {}), ...(opciones.detalle ? { detalle: opciones.detalle } : {}), ...(monto ? { montoUsd: monto } : {}), ...(quien ? { quien } : {}) });
          if (!id) return null;
          idsRef.current = [...idsRef.current, id];
          setIds(idsRef.current);
        }
      }
      throw new Error('Demasiadas autorizaciones seguidas: revisa la operación');
    },
    [pedir, limpiar],
  );

  const dialogo: ReactNode = pedido ? (
    <DialogoAutorizacion
      pedido={pedido}
      cerrar={(id) => {
        pedido.resolver(id);
        setPedido(null);
      }}
    />
  ) : null;

  return { pedir, ejecutar, limpiar, ids, dialogo };
}

function DialogoAutorizacion({ pedido, cerrar }: { pedido: Pedido; cerrar: (id: string | null) => void }) {
  const def = TIPOS_AUTORIZACION[pedido.tipo];
  const [motivo, setMotivo] = useState(pedido.motivo);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [remota, setRemota] = useState<AutorizacionDTO | null>(null);

  const cuerpo = () => ({ tipo: pedido.tipo, motivo: motivo.trim(), ...(pedido.detalle ? { detalle: pedido.detalle } : {}), ...(pedido.montoUsd ? { monto_usd: pedido.montoUsd } : {}) });
  const motivoOk = motivo.trim().length >= 3;

  const conClave = async () => {
    setError(null);
    setEnviando(true);
    try {
      const a = await post<AutorizacionDTO>('/autorizaciones/clave', { ...cuerpo(), email: email.trim(), password });
      toast.success(`Autorizado por ${a.resuelta_por_nombre ?? 'quien autoriza'}`);
      cerrar(a.id);
    } catch (e) {
      setError(mensajeDeError(e));
      setPassword('');
    } finally {
      setEnviando(false);
    }
  };

  const aDistancia = async () => {
    setError(null);
    setEnviando(true);
    try {
      setRemota(await post<AutorizacionDTO>('/autorizaciones', cuerpo()));
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setEnviando(false);
    }
  };

  // Solicitud a distancia: se consulta cada 3 s hasta que la aprueben, la nieguen o venza
  useEffect(() => {
    if (!remota || remota.estado !== 'pendiente') return;
    const t = setInterval(() => {
      get<AutorizacionDTO>(`/autorizaciones/${remota.id}`)
        .then((a) => {
          if (a.estado === 'aprobada') {
            toast.success(`Autorizado por ${a.resuelta_por_nombre ?? 'quien autoriza'}`);
            cerrar(a.id);
          } else if (a.estado !== 'pendiente') setRemota(a);
        })
        .catch(() => undefined);
    }, 3000);
    return () => clearInterval(t);
  }, [remota, cerrar]);

  return (
    <Dialog open onOpenChange={(o) => !o && cerrar(null)}>
      <DialogContent className="max-w-md" data-testid="dialogo-autorizacion">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-mg-verde" /> Requiere autorización</DialogTitle><AyudaVentana tema="autorizacion" /></div>
          <DialogDescription data-testid="autorizacion-quien">{def.etiqueta}. La da {pedido.quien ?? 'quien tiene el permiso'}: con su clave aquí mismo o desde su usuario.</DialogDescription>
        </DialogHeader>
        {pedido.detalle ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 rounded-lg bg-muted/60 px-3 py-2 text-xs" data-testid="autorizacion-detalle">
            {Object.entries(pedido.detalle).filter(([, v]) => v !== null && v !== '').map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="capitalize text-muted-foreground">{k.replace(/_/g, ' ')}</dt>
                <dd className="font-medium">{String(v)}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {remota ? (
          <div className="space-y-2 rounded-lg border px-3 py-3 text-sm" data-testid="autorizacion-espera">
            {remota.estado === 'pendiente' ? (
              <p className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Solicitud enviada. Esperando respuesta de {pedido.quien ?? 'quien puede autorizarla'}…</p>
            ) : remota.estado === 'negada' ? (
              <p className="text-mg-rojo">Negada por {remota.resuelta_por_nombre}: {remota.comentario}</p>
            ) : (
              // Una aprobada que no se usó a tiempo también vence (re-auditoría 2026-10-18, ronda 2, hallazgo 1-H14)
              <p className="text-mg-ambar">{remota.resuelta_por_nombre ? `La autorización de ${remota.resuelta_por_nombre} venció sin usarse: pide una nueva.` : 'La solicitud venció sin respuesta.'}</p>
            )}
          </div>
        ) : (
          <>
            <Campo etiqueta="Motivo" htmlFor="aut-motivo">
              <Input id="aut-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Por qué se necesita" autoFocus={!pedido.motivo} data-testid="autorizacion-motivo" />
            </Campo>
            <div className="space-y-2 rounded-lg border px-3 py-3">
              <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><KeyRound className="h-3.5 w-3.5" /> Con la clave de quien autoriza</p>
              <div className="grid grid-cols-2 gap-2">
                <Input type="email" placeholder="Correo de quien autoriza" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" aria-label="Correo de quien autoriza" data-testid="autorizacion-email" />
                <Input type="password" placeholder="Su contraseña" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" aria-label="Contraseña de quien autoriza" onKeyDown={(e) => { if (e.key === 'Enter' && motivoOk && email && password) void conClave(); }} data-testid="autorizacion-password" />
              </div>
              <Button className="w-full" onClick={() => void conClave()} disabled={!motivoOk || !email.trim() || !password || enviando} cargando={enviando} data-testid="autorizacion-clave">
                Autorizar con clave
              </Button>
            </div>
          </>
        )}
        {error ? <p className="rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-800" role="alert" data-testid="autorizacion-error">{error}</p> : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => cerrar(null)} data-testid="autorizacion-cancelar">Cancelar</Button>
          {!remota ? (
            <Button variant="outline" onClick={() => void aDistancia()} disabled={!motivoOk || enviando} data-testid="autorizacion-remota">
              <Send /> Pedir a distancia
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
